// In-depth MongoDB lessons (Codeazy lesson standard).

export const mongoLessons = [
  {
    slug: "documents-and-crud",
    title: "Documents, Collections and CRUD",
    summary: "How MongoDB stores data, and every create, read, update and delete operation you will use daily, with query and update operators.",
    readTime: 14,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "MongoDB is the “M” in MERN and one of the most popular databases for startups. Its document model maps naturally to JavaScript objects, but writing efficient, correct queries and updates needs a good grasp of its operators. These are the commands you will type every day." },

      { type: "h2", text: "The mental model: folders of JSON files" },
      { type: "p", text: "Think of a **database** as a filing cabinet, a **collection** as a drawer, and each **document** as a JSON file in that drawer. Unlike SQL tables, documents in the same drawer don’t have to share exactly the same fields, and a document can hold nested objects and arrays instead of spreading data across many tables." },
      { type: "table", headers: ["SQL", "MongoDB"], rows: [
        ["Database", "Database"],
        ["Table", "Collection"],
        ["Row", "Document (BSON, a binary form of JSON)"],
        ["Column", "Field"],
        ["Primary key", "`_id` (an automatic ObjectId)"],
        ["JOIN", "Embedding, or `$lookup` in aggregation"]
      ] },
      { type: "code", language: "javascript", code: `// A document in the "students" collection
{
  _id: ObjectId("66f1c2a9e4b0a1b2c3d4e5f6"),
  name: "Asha Rao",
  email: "asha@example.com",
  skills: ["JavaScript", "React"],
  address: { city: "Bengaluru", pin: "560001" },
  enrolledAt: ISODate("2026-09-01T10:00:00Z"),
  scores: { dsa: 82, systemDesign: 74 }
}` },
      { type: "callout", variant: "note", text: "An ObjectId is 12 bytes: a 4-byte timestamp, 5 random bytes and a 3-byte counter. You can get the creation time from it with `_id.getTimestamp()`, and sorting by `_id` roughly sorts by creation time." },

      { type: "h2", text: "Create" },
      { type: "code", language: "javascript", code: `db.students.insertOne({ name: "Asha", email: "asha@example.com", skills: ["React"] });

db.students.insertMany([
  { name: "Ravi", email: "ravi@example.com", skills: ["Java"] },
  { name: "Meera", email: "meera@example.com", skills: ["Python", "SQL"] }
]);` },

      { type: "h2", text: "Read: find with filters" },
      { type: "code", language: "javascript", code: `db.students.find({ "address.city": "Bengaluru" });     // nested field: use quotes
db.students.findOne({ email: "asha@example.com" });

// projection: choose which fields to return
db.students.find({}, { name: 1, email: 1, _id: 0 });

// sort, skip and limit
db.students.find().sort({ enrolledAt: -1 }).skip(20).limit(10);

db.students.countDocuments({ skills: "React" });` },
      { type: "table", headers: ["Operator", "Example", "Matches"], rows: [
        ["`$eq` / `$ne`", "`{ level: { $ne: \"advanced\" } }`", "Equal / not equal"],
        ["`$gt` `$gte` `$lt` `$lte`", "`{ \"scores.dsa\": { $gte: 80 } }`", "Comparisons"],
        ["`$in` / `$nin`", "`{ city: { $in: [\"Pune\", \"Delhi\"] } }`", "Any of / none of"],
        ["`$and` / `$or`", "`{ $or: [{ city: \"Pune\" }, { remote: true }] }`", "Logical combinations"],
        ["`$exists`", "`{ phone: { $exists: false } }`", "Field missing"],
        ["`$regex`", "`{ name: { $regex: \"^as\", $options: \"i\" } }`", "Pattern (anchored patterns can use an index)"],
        ["Array value", "`{ skills: \"React\" }`", "The array **contains** \"React\""],
        ["`$all`", "`{ skills: { $all: [\"React\", \"Node\"] } }`", "Contains all of them"],
        ["`$size`", "`{ skills: { $size: 3 } }`", "Array length is exactly 3"],
        ["`$elemMatch`", "`{ results: { $elemMatch: { subject: \"dsa\", score: { $gte: 80 } } } }`", "One element satisfies every condition"]
      ] },
      { type: "callout", variant: "warning", text: "`{ \"results.subject\": \"dsa\", \"results.score\": { $gte: 80 } }` can match a student whose DSA score is 50 but who has some **other** result of 90. The two conditions can be met by different array elements. Use `$elemMatch` when both conditions must hold for the same element." },

      { type: "h2", text: "Update" },
      { type: "code", language: "javascript", code: `db.students.updateOne(
  { email: "asha@example.com" },           // filter
  {
    $set: { "address.city": "Pune" },      // set or overwrite a field
    $inc: { "scores.dsa": 5 },             // increment
    $push: { skills: "Node.js" },          // append to an array
    $currentDate: { updatedAt: true }
  }
);

db.students.updateMany({ skills: "React" }, { $set: { track: "frontend" } });` },
      { type: "table", headers: ["Operator", "Does"], rows: [
        ["`$set` / `$unset`", "Set a field / remove a field"],
        ["`$inc`", "Increment or decrement a number atomically"],
        ["`$push` / `$pull`", "Add to an array / remove matching elements"],
        ["`$addToSet`", "Add to an array only if not already present"],
        ["`$push` + `$each` + `$slice`", "Append many and keep only the last N (e.g. recent activity)"],
        ["`$` / `$[]` / `$[id]`", "Update the matched, all, or filtered array elements"],
        ["`$setOnInsert`", "Set fields only when an upsert creates a new document"]
      ] },
      { type: "h3", text: "Upsert: update or insert" },
      { type: "code", language: "javascript", code: `// Track daily lesson views: create the document on the first view, increment after that
db.lessonStats.updateOne(
  { lessonId: "closures", date: "2026-10-12" },
  { $inc: { views: 1 }, $setOnInsert: { createdAt: new Date() } },
  { upsert: true }
);` },
      { type: "h3", text: "Get the updated document back" },
      { type: "code", language: "javascript", code: `const updated = db.students.findOneAndUpdate(
  { email: "asha@example.com" },
  { $inc: { credits: -1 } },
  { returnDocument: "after" }      // default is "before"
);` },

      { type: "h2", text: "Delete" },
      { type: "code", language: "javascript", code: `db.students.deleteOne({ email: "old@example.com" });
db.sessions.deleteMany({ expiresAt: { $lt: new Date() } });

// Often better: soft delete, so data can be recovered and audited
db.students.updateOne({ _id: id }, { $set: { deletedAt: new Date() } });` },
      { type: "callout", variant: "warning", text: "`deleteMany({})` with an empty filter deletes **every document** in the collection. Always run the same filter with `find()` or `countDocuments()` first." },

      { type: "h2", text: "Why atomic operators matter" },
      { type: "compare", badLabel: "Read, modify, write (race condition)", bad: `const course = await db.courses.findOne({ _id: id });
course.seatsLeft = course.seatsLeft - 1;
await db.courses.replaceOne({ _id: id }, course);
// two users at once → both read 1 seat → both book it`, goodLabel: "Atomic conditional update", good: `const result = await db.courses.updateOne(
  { _id: id, seatsLeft: { $gt: 0 } },   // condition
  { $inc: { seatsLeft: -1 } }            // atomic change
);
if (result.modifiedCount === 0) {
  throw new Error("Batch is full");
}` },
      { type: "p", text: "Single-document updates in MongoDB are atomic. Putting the condition in the filter and the change in an operator makes overbooking impossible, with no locks needed." },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "What is the difference between MongoDB and a relational database?", answer: ["MongoDB stores flexible JSON-like documents in collections. Related data is often embedded, the schema is enforced by the application or by schema validation, and it scales horizontally with sharding. Relational databases store rows in tables with fixed schemas, normalise data and use JOINs, and have mature multi-row transactions and constraints. MongoDB also supports multi-document transactions, but they are best kept to a minimum."] },
      { type: "qa", question: "What does `$elemMatch` do and when do you need it?", answer: ["It matches documents where at least one array element satisfies all the given conditions together. Without it, conditions on fields of array elements can be met by different elements."] },
      { type: "qa", question: "What is an upsert?", answer: ["An update with `{ upsert: true }` that inserts a new document if no document matches the filter. `$setOnInsert` sets fields only when an insert happens. It is ideal for counters and “create if missing” logic."] },
      { type: "qa", question: "Are MongoDB operations atomic?", answer: ["Every write to a single document is atomic, including changes to embedded documents and arrays. Operations across several documents aren’t atomic together unless you use a multi-document transaction (on a replica set or sharded cluster)."] },
      { type: "qa", question: "What is the difference between `$push` and `$addToSet`?", answer: ["`$push` always appends the value, so duplicates are possible. `$addToSet` appends only if the value isn’t already in the array, which makes it ideal for tags, likes or enrolled course IDs."] },

      { type: "exercise", title: "Write the queries", task: "Using a `students` collection like the example above, write queries to: (1) find students in Pune or Delhi with a DSA score of at least 75, returning only name and email, sorted by score, highest first; (2) add \"Docker\" to Ravi’s skills only if it is missing; (3) remove the skill \"jQuery\" from every student.", language: "javascript", solution: `// 1
db.students
  .find(
    { "address.city": { $in: ["Pune", "Delhi"] }, "scores.dsa": { $gte: 75 } },
    { name: 1, email: 1, _id: 0 }
  )
  .sort({ "scores.dsa": -1 });

// 2
db.students.updateOne({ name: "Ravi" }, { $addToSet: { skills: "Docker" } });

// 3
db.students.updateMany({ skills: "jQuery" }, { $pull: { skills: "jQuery" } });`, explanation: "Nested fields use dot notation in quotes. `$addToSet` prevents duplicates without having to check first. Filtering on `{ skills: \"jQuery\" }` in the third query means MongoDB only touches documents that actually contain it." },

      { type: "summary", items: [
        "Databases contain collections, which contain JSON-like documents identified by `_id`.",
        "Learn the query operators: comparisons, `$in`, `$or`, `$exists`, array matching and `$elemMatch`.",
        "Use update operators (`$set`, `$inc`, `$push`, `$addToSet`, `$pull`) instead of replacing documents.",
        "Use upserts for “create or update”, and `findOneAndUpdate` to get the result back.",
        "Single-document writes are atomic, so put conditions in the filter to avoid race conditions."
      ] }
    ]
  },

  {
    slug: "queries-and-indexes",
    title: "Queries and Indexes",
    summary: "Make queries fast: how indexes work, compound index design (the ESR rule), explain plans and common traps.",
    readTime: 14,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "A query that takes 5ms with 1,000 documents can take 5 seconds with 10 million if there is no index. Indexes are the single biggest factor in database performance, and “how would you speed up this query?” is a favourite interview question." },

      { type: "h2", text: "The mental model: a book’s index" },
      { type: "p", text: "Without an index, finding every mention of “closures” in a 900-page book means reading every page: a **collection scan** (COLLSCAN). The index at the back lists terms alphabetically with page numbers, so you jump straight there: an **index scan** (IXSCAN). MongoDB indexes are B-tree structures that keep field values sorted, each pointing to its document." },
      { type: "table", headers: ["", "Without an index", "With an index"], rows: [
        ["How it searches", "Reads every document", "Walks a sorted tree"],
        ["Complexity", "O(n)", "O(log n)"],
        ["Cost", "Slow reads", "Faster reads, slightly slower writes and extra storage"]
      ] },

      { type: "h2", text: "Creating indexes" },
      { type: "code", language: "javascript", code: `// Single field
db.students.createIndex({ email: 1 }, { unique: true });   // also enforces uniqueness

// Compound: order matters
db.orders.createIndex({ userId: 1, createdAt: -1 });

// Multikey: indexing an array field indexes every element
db.students.createIndex({ skills: 1 });

// Text search
db.courses.createIndex({ title: "text", description: "text" });

// TTL: delete documents automatically after a time
db.sessions.createIndex({ createdAt: 1 }, { expireAfterSeconds: 60 * 60 * 24 });

// Partial: index only the documents you query
db.orders.createIndex({ createdAt: -1 }, { partialFilterExpression: { status: "pending" } });

db.students.getIndexes();` },

      { type: "h2", text: "Reading explain() output" },
      { type: "code", language: "javascript", code: `db.orders.find({ userId: 42, status: "paid" })
  .sort({ createdAt: -1 })
  .explain("executionStats");` },
      { type: "table", headers: ["Field", "What to look for"], rows: [
        ["`winningPlan.stage`", "`IXSCAN` is good. `COLLSCAN` on a large collection is bad"],
        ["`SORT` stage present", "An in-memory sort happened because no index supplied the order (slow, and memory-limited)"],
        ["`totalDocsExamined`", "Should be close to `nReturned`"],
        ["`totalKeysExamined`", "Index entries scanned. Ideally close to `nReturned`"],
        ["`executionTimeMillis`", "Total time"]
      ] },
      { type: "callout", variant: "tip", text: "The key health ratio is **documents examined ÷ documents returned**. A query returning 10 documents after examining 500,000 needs a better index." },

      { type: "h2", text: "Compound indexes and the ESR rule" },
      { type: "p", text: "A compound index sorts by the first field, then by the second within it, and so on, like a phone book sorted by surname and then first name. Order your fields using the **ESR rule**:" },
      { type: "ol", items: [
        "**E**quality fields first: fields matched exactly (`status: \"paid\"`, `userId: 42`).",
        "**S**ort fields next: fields in `.sort()` (`createdAt: -1`).",
        "**R**ange fields last: `$gt`, `$lt`, `$in` with many values, regex (`amount: { $gte: 500 }`)."
      ] },
      { type: "code", language: "javascript", code: `// Query
db.orders.find({ userId: 42, status: "paid", amount: { $gte: 500 } })
         .sort({ createdAt: -1 });

// ESR index: Equality (userId, status) → Sort (createdAt) → Range (amount)
db.orders.createIndex({ userId: 1, status: 1, createdAt: -1, amount: 1 });` },
      { type: "h3", text: "Index prefixes" },
      { type: "p", text: "A compound index on `{ a: 1, b: 1, c: 1 }` also serves queries on its **prefixes**: `{ a }` and `{ a, b }`. It can’t efficiently serve `{ b }` or `{ c }` alone, just as a phone book sorted by surname doesn’t help you find everyone called “Ravi”." },
      { type: "table", headers: ["Query filters on", "Uses index { a, b, c }?"], rows: [
        ["`a`", "Yes (prefix)"],
        ["`a, b`", "Yes (prefix)"],
        ["`a, b, c`", "Yes"],
        ["`b` or `b, c`", "No"],
        ["`a, c`", "Partially: `a` uses the index, and `c` is then filtered from the matching entries"]
      ] },

      { type: "h2", text: "Covered queries" },
      { type: "p", text: "If the filter **and** every returned field are in the index, MongoDB answers from the index alone without reading the documents:" },
      { type: "code", language: "javascript", code: `db.students.createIndex({ email: 1, name: 1 });

db.students.find({ email: "asha@example.com" }, { email: 1, name: 1, _id: 0 });
// explain → totalDocsExamined: 0 (covered)` },

      { type: "h2", text: "Common index traps" },
      { type: "compare", badLabel: "Unanchored, case-insensitive regex", bad: `db.courses.find({
  title: { $regex: "design", $options: "i" }
});
// scans every index key or document`, goodLabel: "Text index or anchored prefix", good: `// full-text search
db.courses.find({ $text: { $search: "design" } });

// or prefix search on a lowercased field
db.courses.find({ titleLower: { $regex: "^sys" } });` },
      { type: "compare", badLabel: "Deep skip pagination", bad: `db.posts.find().sort({ _id: -1 })
  .skip(100000).limit(20);
// still walks 100,000 entries`, goodLabel: "Range (cursor) pagination", good: `db.posts.find({ _id: { $lt: lastSeenId } })
  .sort({ _id: -1 })
  .limit(20);
// jumps straight to the position` },
      { type: "ul", items: [
        "**Too many indexes:** every insert and update must update every index. Remove unused ones (check `$indexStats`).",
        "**Low-selectivity fields alone:** an index on a boolean like `isActive` matches half the collection. Combine it with selective fields instead.",
        "**Wrong field order:** an index on `{ createdAt: 1, userId: 1 }` doesn’t help “orders for user 42”. Remember ESR.",
        "**Huge multikey indexes:** indexing two array fields in the same compound index isn’t allowed, and large arrays create many index entries."
      ] },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "How do indexes speed up queries, and what do they cost?", answer: ["An index keeps field values sorted in a B-tree with pointers to documents, so MongoDB can find matches in O(log n) instead of scanning everything, and can return sorted results without an in-memory sort. The cost is extra storage, RAM for the working set and slower writes, because every index must be updated."] },
      { type: "qa", question: "Explain the ESR rule.", answer: ["When designing a compound index, put fields used for Equality matches first, then fields used for Sorting, then fields used in Range filters. This lets the index narrow the results exactly, return them already sorted, and then scan only the needed range."] },
      { type: "qa", question: "How do you find out why a query is slow?", answer: ["Run `.explain(\"executionStats\")` and check for COLLSCAN, in-memory SORT stages and a high ratio of documents examined to documents returned. Use the profiler or Atlas Performance Advisor to find slow queries, then add or adjust indexes, or reshape the query."] },
      { type: "qa", question: "What is a covered query?", answer: ["A query where the filter fields and all projected fields are part of one index, and `_id` is excluded unless it is indexed, so MongoDB returns results from the index without fetching documents. `totalDocsExamined` is 0."] },
      { type: "qa", question: "Why is skip/limit pagination slow for deep pages?", answer: ["`skip(n)` still walks past n index entries or documents, so the cost grows with page depth. Range-based (cursor) pagination filters with `_id < lastSeenId` or a similar indexed field and jumps straight to the right place, so every page costs the same."] },
      { type: "qa", question: "What is a TTL index?", answer: ["An index on a date field with `expireAfterSeconds`. A background task deletes documents once that many seconds have passed since the date. It is ideal for sessions, OTPs, logs and temporary data."] },

      { type: "exercise", title: "Design the index", task: "The Codeazy admin dashboard runs this query constantly and it has become slow: `db.classRequests.find({ course: \"DSA\", status: \"pending\", createdAt: { $gte: lastWeek } }).sort({ createdAt: -1 })`. Design the best index and explain why. Then write the query you would use to confirm it.", language: "javascript", solution: `// Equality: course, status → Sort: createdAt → Range: createdAt (same field)
db.classRequests.createIndex({ course: 1, status: 1, createdAt: -1 });

// Verify
db.classRequests
  .find({ course: "DSA", status: "pending", createdAt: { $gte: lastWeek } })
  .sort({ createdAt: -1 })
  .explain("executionStats");
// expect: IXSCAN, no SORT stage, totalDocsExamined ≈ nReturned`, explanation: "`course` and `status` are equality matches, so they go first. `createdAt` is used for both the sort and the range. Because it follows the equality fields, the index both narrows the date range and returns documents already in sorted order, so no in-memory sort is needed." },

      { type: "summary", items: [
        "Indexes are a book’s index: sorted B-trees that turn O(n) scans into O(log n) lookups.",
        "Use `explain(\"executionStats\")` and watch for COLLSCAN, SORT stages and a high docs-examined ratio.",
        "Order compound index fields by Equality, Sort, Range. Prefixes of an index are reusable.",
        "Use unique, TTL, partial, text and covered queries where they fit.",
        "Avoid unanchored regex, deep `skip` pagination and unused indexes."
      ] }
    ]
  },

  {
    slug: "data-modeling",
    title: "Data Modeling: Embed vs Reference",
    summary: "Design schemas around your queries: embedding, referencing, one-to-many patterns and proven schema patterns.",
    readTime: 13,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "In MongoDB, schema design matters more than in SQL, because there is no single normalised “right answer”. A good model makes your common queries a single fast read. A bad one leads to dozens of lookups, giant documents and painful migrations." },

      { type: "h2", text: "The mental model: design for how you read" },
      { type: "p", text: "In SQL, you model the **data** (normalise it, then JOIN at query time). In MongoDB, you model the **access patterns**: list the screens and API calls your app makes, then shape documents so each common request needs one read. The rule of thumb is: **data that is accessed together should be stored together.**" },

      { type: "h2", text: "Embedding" },
      { type: "code", language: "javascript", code: `// A course with its modules embedded: one read renders the whole syllabus page
{
  _id: "system-design",
  title: "System Design",
  modules: [
    { order: 1, title: "Fundamentals", topics: ["Requirements", "Estimation"] },
    { order: 2, title: "Caching", topics: ["Strategies", "Eviction"] }
  ]
}` },
      { type: "h2", text: "Referencing" },
      { type: "code", language: "javascript", code: `// students
{ _id: ObjectId("s1"), name: "Asha", enrolledCourseIds: ["dsa", "system-design"] }

// enrollments: many per student and per course, with their own data
{ _id: ObjectId("e1"), studentId: ObjectId("s1"), courseId: "dsa", progress: 0.42, enrolledAt: ISODate(...) }` },

      { type: "h2", text: "How to decide" },
      { type: "table", headers: ["Question", "Leans embed", "Leans reference"], rows: [
        ["Is the data read together?", "Almost always", "Often separately"],
        ["How many related items?", "Few and bounded (“one-to-few”)", "Many or unbounded (“one-to-zillions”)"],
        ["Does the child change often, independently?", "Rarely", "Frequently"],
        ["Is the child shared by many parents?", "No", "Yes (e.g. a course shared by students)"],
        ["Does the child need its own queries?", "No", "Yes (e.g. “all comments by user X”)"]
      ] },
      { type: "callout", variant: "warning", text: "A document can be at most **16 MB**. Unbounded embedded arrays, such as every comment on a viral post or every log entry, eventually hit that limit, and slow down every read long before they do." },

      { type: "h2", text: "One-to-many patterns" },
      { type: "table", headers: ["Relationship", "Example", "Pattern"], rows: [
        ["One-to-few", "A user’s 2–3 addresses", "Embed an array in the parent"],
        ["One-to-many", "A course’s ~200 reviews", "Child documents with a `courseId` reference (+ index), or embed only the latest few"],
        ["One-to-zillions", "A server’s millions of log lines", "Children reference the parent. Never store an array in the parent"],
        ["Many-to-many", "Students ↔ courses", "A join collection (`enrollments`), or arrays of IDs on one side if small"]
      ] },

      { type: "h2", text: "Proven schema patterns" },
      { type: "h3", text: "Extended reference: copy what you display" },
      { type: "code", language: "javascript", code: `// order stores a snapshot of the fields needed to show it, so no lookup is needed
{
  _id: ObjectId("o1"),
  user: { _id: ObjectId("s1"), name: "Asha Rao" },        // copied subset
  items: [{ courseId: "dsa", title: "DSA", price: 4999 }], // price at purchase time
  total: 4999
}` },
      { type: "p", text: "Duplicating a few fields is fine when they rarely change, or when you want a historical snapshot such as the price at purchase time. Update the copies in the background if the source changes." },
      { type: "h3", text: "Subset: embed the hot part" },
      { type: "code", language: "javascript", code: `// course page shows only the 5 latest reviews, and the rest live in "reviews"
{
  _id: "dsa",
  title: "DSA",
  ratingAvg: 4.7,
  ratingCount: 1284,
  recentReviews: [ /* latest 5 only, kept with $push + $slice: -5 */ ]
}` },
      { type: "h3", text: "Computed: pre-calculate expensive values" },
      { type: "code", language: "javascript", code: `// When a review is added, update the counters atomically
db.courses.updateOne(
  { _id: "dsa" },
  { $inc: { ratingCount: 1, ratingSum: 5 } }
);
// ratingAvg = ratingSum / ratingCount, so there's no need to scan all reviews` },
      { type: "h3", text: "Bucket: group many small items" },
      { type: "code", language: "javascript", code: `// One document per student per day instead of one per event
{
  studentId: ObjectId("s1"),
  day: "2026-10-12",
  events: [ { t: "10:02", lesson: "closures" }, { t: "10:20", lesson: "event-loop" } ],
  count: 2
}` },
      { type: "h3", text: "Polymorphic: different shapes, one collection" },
      { type: "code", language: "javascript", code: `// "content" holds lessons, quizzes and videos, distinguished by a type field
{ type: "lesson", title: "Closures", readTime: 12 }
{ type: "quiz", title: "Closures quiz", questions: [ /* ... */ ] }
{ type: "video", title: "Event loop explained", durationSec: 640 }` },

      { type: "h2", text: "Schema validation" },
      { type: "p", text: "Flexible doesn’t have to mean unvalidated. Enforce rules at the database level with JSON Schema, or in the application with Mongoose (next lessons):" },
      { type: "code", language: "javascript", code: `db.createCollection("students", {
  validator: {
    $jsonSchema: {
      bsonType: "object",
      required: ["name", "email"],
      properties: {
        name: { bsonType: "string", minLength: 2 },
        email: { bsonType: "string", pattern: "^.+@.+$" },
        skills: { bsonType: "array", maxItems: 50, items: { bsonType: "string" } }
      }
    }
  }
});` },

      { type: "h2", text: "Common mistakes" },
      { type: "compare", badLabel: "SQL-style over-normalisation", bad: `// rendering one course page needs 4 queries
courses:     { _id, title }
modules:     { _id, courseId, title }
topics:      { _id, moduleId, name }
instructors: { _id, courseId, name }`, goodLabel: "Shaped for the page", good: `// one query renders the page
courses: {
  _id, title,
  instructor: { name, photo },
  modules: [{ title, topics: [...] }]
}` },
      { type: "compare", badLabel: "Unbounded embedded array", bad: `// every view ever, inside the course document
{ _id: "dsa", views: [ { userId, at }, ... ] }`, goodLabel: "Separate collection + counter", good: `courses: { _id: "dsa", viewCount: 120394 }
views:   { courseId: "dsa", userId, at }   // TTL or bucketed` },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "When would you embed vs reference in MongoDB?", answer: ["Embed when the data is read together, is bounded in size and belongs to a single parent, such as addresses or order line items. Reference when the related data is large or unbounded, changes independently, is shared by many documents, or needs to be queried on its own, such as reviews, users or logs."] },
      { type: "qa", question: "How do you model a many-to-many relationship?", answer: ["Either keep arrays of IDs on one or both sides when the lists are small and bounded, or use a join collection such as `enrollments` with `studentId` and `courseId`, indexed on both. The join collection is better when the relationship has its own data, such as progress or enrolment date, or can grow large."] },
      { type: "qa", question: "Isn’t duplicating data bad?", answer: ["Not necessarily. Duplicating small, rarely changing fields (the extended reference pattern) avoids lookups on hot read paths. Some duplication is intentional, such as a price snapshot in an order. The trade-off is keeping copies in sync when the source changes, so duplicate only stable fields."] },
      { type: "qa", question: "What is the 16 MB limit and how do you avoid it?", answer: ["The maximum BSON document size. Avoid it by not embedding unbounded arrays: use separate collections, the subset pattern for recent items, or the bucket pattern for time series. Store files in object storage such as S3 or GridFS instead of documents."] },
      { type: "qa", question: "How does MongoDB enforce a schema?", answer: ["Through collection-level JSON Schema validators (`$jsonSchema`), unique and other index constraints, and application-level validation with libraries such as Mongoose or Zod. Validation level and action settings control whether invalid writes are rejected or just warned about."] },

      { type: "exercise", title: "Model a course discussion forum", task: "Codeazy wants a Q&A forum per lesson. The lesson page shows the 10 newest questions with their author names and answer counts. A question page shows the question and all its answers (popular ones can have hundreds). Users can upvote answers. Design the collections and example documents, and list the indexes.", language: "javascript", solution: `// questions: one per question; author snapshot avoids a lookup on the list page
{
  _id: ObjectId("q1"),
  lessonId: "javascript/closures",
  author: { _id: ObjectId("s1"), name: "Asha Rao" },   // extended reference
  title: "Why does my counter reset?",
  body: "...",
  answerCount: 37,                                       // computed pattern
  createdAt: ISODate("2026-10-12T09:00:00Z")
}

// answers: separate, because they're unbounded and voted on independently
{
  _id: ObjectId("a1"),
  questionId: ObjectId("q1"),
  author: { _id: ObjectId("s2"), name: "Ravi" },
  body: "...",
  votes: 12,
  createdAt: ISODate("2026-10-12T09:30:00Z")
}

// votes: one per user per answer, so a user can't vote twice
{ answerId: ObjectId("a1"), userId: ObjectId("s3") }

// Indexes
db.questions.createIndex({ lessonId: 1, createdAt: -1 });          // lesson page list
db.answers.createIndex({ questionId: 1, votes: -1 });              // answers by popularity
db.votes.createIndex({ answerId: 1, userId: 1 }, { unique: true }); // one vote each`, explanation: "The lesson page reads only `questions`, with the author name and answer count embedded. Answers are unbounded and updated independently, so they get their own collection. A unique index on `votes` enforces one vote per user, and `$inc` keeps `votes` and `answerCount` correct atomically." },

      { type: "summary", items: [
        "Model for your access patterns: data read together is stored together.",
        "Embed bounded, owned, co-read data. Reference unbounded, shared or independently queried data.",
        "Respect the 16 MB limit, and never let arrays grow without bounds.",
        "Use schema patterns: extended reference, subset, computed, bucket and polymorphic.",
        "Enforce structure with `$jsonSchema` or Mongoose, plus unique indexes."
      ] }
    ]
  },

  {
    slug: "aggregation",
    title: "The Aggregation Pipeline",
    summary: "Transform and analyse data in the database: $match, $group, $lookup, $unwind, $facet and real reporting queries.",
    readTime: 14,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Dashboards, reports, leaderboards and analytics, such as “revenue per course this month” or “top 10 students by score”, need data to be grouped, joined and reshaped. The aggregation pipeline does this inside the database, which is far faster than pulling every document into Node and looping." },

      { type: "h2", text: "The mental model: an assembly line" },
      { type: "p", text: "Documents travel along a **conveyor belt** through stations (stages). Each station does one job, such as filtering, reshaping, grouping or sorting, and passes its output to the next. The order of stations matters, just as on a real assembly line." },
      { type: "code", language: "text", code: `orders ─▶ [$match: paid, this month] ─▶ [$group: by course, sum price] ─▶ [$sort: revenue desc] ─▶ [$limit: 5] ─▶ results` },

      { type: "h2", text: "The most-used stages" },
      { type: "table", headers: ["Stage", "SQL equivalent", "Does"], rows: [
        ["`$match`", "WHERE", "Filter documents (uses indexes when early in the pipeline)"],
        ["`$project` / `$addFields` / `$set`", "SELECT", "Pick, rename or compute fields"],
        ["`$group`", "GROUP BY", "Group by a key and compute accumulators"],
        ["`$sort`, `$skip`, `$limit`", "ORDER BY, OFFSET, LIMIT", "Order and paginate"],
        ["`$lookup`", "LEFT JOIN", "Pull in matching documents from another collection"],
        ["`$unwind`", "", "Turn one document with an array into one document per element"],
        ["`$count`", "COUNT(*)", "Count the documents reaching this stage"],
        ["`$facet`", "", "Run several sub-pipelines on the same input"],
        ["`$bucket`", "", "Group into ranges (histograms)"]
      ] },

      { type: "h2", text: "Example: revenue per course" },
      { type: "code", language: "javascript", code: `// orders: { userId, courseId, amount, status, createdAt }
db.orders.aggregate([
  { $match: {
      status: "paid",
      createdAt: { $gte: ISODate("2026-10-01"), $lt: ISODate("2026-11-01") }
  } },
  { $group: {
      _id: "$courseId",                 // group key ("$" means "value of field")
      revenue: { $sum: "$amount" },
      orders: { $sum: 1 },
      avgOrder: { $avg: "$amount" }
  } },
  { $sort: { revenue: -1 } },
  { $limit: 5 }
]);`, output: `[
  { _id: "fullstack", revenue: 1349730, orders: 270, avgOrder: 4999 },
  { _id: "dsa",       revenue:  899820, orders: 180, avgOrder: 4999 },
  ...
]` },
      { type: "table", headers: ["Accumulator", "Computes"], rows: [
        ["`$sum`", "A total, or a count with `$sum: 1`"],
        ["`$avg`, `$min`, `$max`", "Statistics"],
        ["`$push`", "An array of all values in the group"],
        ["`$addToSet`", "An array of unique values"],
        ["`$first` / `$last`", "Value from the first or last document (sort first!)"]
      ] },

      { type: "h2", text: "Joining with $lookup" },
      { type: "code", language: "javascript", code: `db.orders.aggregate([
  { $match: { status: "paid" } },
  { $lookup: {
      from: "courses",            // other collection
      localField: "courseId",     // field in orders
      foreignField: "_id",        // field in courses
      as: "course"                // output array field
  } },
  { $unwind: "$course" },         // array of 1 → embedded object
  { $project: {
      _id: 0,
      courseTitle: "$course.title",
      amount: 1,
      createdAt: 1
  } }
]);` },
      { type: "callout", variant: "tip", text: "Make sure the `foreignField` is indexed (here `courses._id` always is). `$lookup` without an index on the joined field runs a collection scan for every input document." },

      { type: "h2", text: "$unwind: working with arrays" },
      { type: "code", language: "javascript", code: `// students: { name, skills: ["React", "Node", "SQL"] }
// "Which skills are most common?"
db.students.aggregate([
  { $unwind: "$skills" },                               // one doc per skill
  { $group: { _id: "$skills", students: { $sum: 1 } } },
  { $sort: { students: -1 } },
  { $limit: 10 }
]);`, output: `[ { _id: "JavaScript", students: 812 }, { _id: "React", students: 640 }, ... ]` },

      { type: "h2", text: "$facet: a whole dashboard in one query" },
      { type: "code", language: "javascript", code: `db.classRequests.aggregate([
  { $match: { createdAt: { $gte: ISODate("2026-10-01") } } },
  { $facet: {
      byCourse: [
        { $group: { _id: "$course", count: { $sum: 1 } } },
        { $sort: { count: -1 } }
      ],
      byStatus: [
        { $group: { _id: "$status", count: { $sum: 1 } } }
      ],
      total: [ { $count: "value" } ],
      latest: [ { $sort: { createdAt: -1 } }, { $limit: 5 }, { $project: { name: 1, course: 1 } } ]
  } }
]);` },

      { type: "h2", text: "Dates and computed fields" },
      { type: "code", language: "javascript", code: `// Daily sign-ups for a chart
db.students.aggregate([
  { $match: { enrolledAt: { $gte: ISODate("2026-09-01") } } },
  { $group: {
      _id: { $dateToString: { format: "%Y-%m-%d", date: "$enrolledAt", timezone: "Asia/Kolkata" } },
      signups: { $sum: 1 }
  } },
  { $sort: { _id: 1 } }
]);

// Computed fields with conditions
{ $addFields: {
    grade: { $switch: {
      branches: [
        { case: { $gte: ["$score", 85] }, then: "A" },
        { case: { $gte: ["$score", 70] }, then: "B" }
      ],
      default: "C"
    } }
} }` },

      { type: "h2", text: "Performance rules" },
      { type: "ol", items: [
        "**`$match` as early as possible.** Only an early `$match` (and `$sort`) can use indexes, and it shrinks the data for every later stage.",
        "**`$project` away unneeded fields early** when documents are large.",
        "**`$sort` + `$limit` together.** MongoDB keeps only the top N in memory.",
        "Stages are limited to 100 MB of RAM each. Use `{ allowDiskUse: true }` for large sorts and groups (allowed by default in recent versions).",
        "For heavy, repeated reports, store results with `$merge` into a summary collection on a schedule."
      ] },
      { type: "compare", badLabel: "Filter after an expensive join", bad: `[
  { $lookup: { from: "courses", ... } },
  { $unwind: "$course" },
  { $match: { status: "paid" } }
]`, goodLabel: "Filter first", good: `[
  { $match: { status: "paid" } },
  { $lookup: { from: "courses", ... } },
  { $unwind: "$course" }
]` },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "What is the aggregation pipeline?", answer: ["A framework for processing documents through an ordered sequence of stages, such as `$match`, `$group`, `$lookup` and `$project`. Each stage transforms the stream of documents and passes its output to the next, which enables filtering, grouping, joins and reshaping inside the database."] },
      { type: "qa", question: "How do you join collections in MongoDB?", answer: ["With the `$lookup` stage, which performs a left outer join by matching `localField` to `foreignField` in the other collection, or with a pipeline for complex conditions. The results come back as an array, often flattened with `$unwind`. Index the foreign field, and prefer embedding if you join constantly on hot paths."] },
      { type: "qa", question: "Why should `$match` come first?", answer: ["Early `$match` stages can use indexes and reduce the number of documents flowing through every later stage, which lowers CPU, memory and time. A `$match` after `$group` or `$lookup` can’t use collection indexes."] },
      { type: "qa", question: "What does `$unwind` do?", answer: ["It deconstructs an array field, producing one output document per element, with that element in place of the array. It is used to group or count by array values, or to flatten `$lookup` results. `preserveNullAndEmptyArrays` keeps documents whose array is empty or missing."] },
      { type: "qa", question: "What is `$facet` used for?", answer: ["Running several independent sub-pipelines over the same filtered input in one query, for example search results plus category counts plus price buckets for a filter sidebar, or several dashboard widgets at once."] },

      { type: "exercise", title: "Build a course leaderboard", task: "Given `submissions: { studentId, courseId, score, submittedAt }` and `students: { _id, name }`, write a pipeline that returns the top 3 students in the \"dsa\" course by **average** score (only students with at least 5 submissions), showing their name, average score rounded to 1 decimal place, and submission count.", language: "javascript", solution: `db.submissions.aggregate([
  { $match: { courseId: "dsa" } },                       // 1. filter early (index: courseId)
  { $group: {
      _id: "$studentId",
      avgScore: { $avg: "$score" },
      submissions: { $sum: 1 }
  } },
  { $match: { submissions: { $gte: 5 } } },              // 2. HAVING count >= 5
  { $sort: { avgScore: -1 } },
  { $limit: 3 },                                         // 3. top 3 before joining
  { $lookup: { from: "students", localField: "_id", foreignField: "_id", as: "student" } },
  { $unwind: "$student" },
  { $project: {
      _id: 0,
      name: "$student.name",
      avgScore: { $round: ["$avgScore", 1] },
      submissions: 1
  } }
]);`, output: `[
  { submissions: 14, name: "Meera", avgScore: 93.4 },
  { submissions: 9,  name: "Asha",  avgScore: 91.2 },
  { submissions: 22, name: "Ravi",  avgScore: 88.7 }
]`, explanation: "Filter first, group, apply the “HAVING”-style filter, sort, and limit to 3 **before** the `$lookup`, so you join only 3 documents instead of every student. The second `$match` works on the grouped results." },

      { type: "summary", items: [
        "The pipeline is an assembly line of stages, and the order matters.",
        "Use `$match` → `$group` → `$sort` → `$limit` for most reports, with `$sum`, `$avg` and `$push` accumulators.",
        "Use `$lookup` (with an indexed foreign field) to join, and `$unwind` to flatten arrays.",
        "`$facet` builds several dashboard widgets in one query.",
        "Match early, limit before expensive joins, and pre-compute heavy reports with `$merge`."
      ] }
    ]
  },

  {
    slug: "mongoose",
    title: "Mongoose in Practice",
    summary: "Schemas, validation, relationships with populate, middleware hooks, lean queries and transactions in a Node.js app.",
    readTime: 13,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Most Node + MongoDB projects, including the Codeazy backend itself, use Mongoose. It adds schemas, validation, relationships and hooks on top of the driver. Using it well, and knowing where it costs performance, is a core MERN skill." },

      { type: "h2", text: "The mental model: a blueprint with a gatekeeper" },
      { type: "p", text: "A Mongoose **schema** is a blueprint that says what a document should look like. A **model** is the gatekeeper built from it: every document going in through the model is checked, defaults are filled in and hooks run, and the model gives you methods to query the collection." },

      { type: "h2", text: "Defining a schema and model" },
      { type: "code", language: "javascript", code: `import mongoose from "mongoose";

const courseSchema = new mongoose.Schema(
  {
    title: { type: String, required: [true, "Title is required"], trim: true, maxlength: 120 },
    slug: { type: String, required: true, unique: true, lowercase: true },
    level: { type: String, enum: ["beginner", "intermediate", "advanced"], default: "beginner" },
    price: { type: Number, min: 0, required: true },
    tags: { type: [String], default: [] },
    instructor: { type: mongoose.Schema.Types.ObjectId, ref: "User", required: true },
    isPublished: { type: Boolean, default: false, index: true }
  },
  { timestamps: true }   // adds createdAt and updatedAt automatically
);

courseSchema.index({ level: 1, createdAt: -1 });   // compound index

export const Course = mongoose.model("Course", courseSchema);  // collection: "courses"` },
      { type: "table", headers: ["Option", "Effect"], rows: [
        ["`required`, `min`, `max`, `minlength`, `maxlength`, `enum`, `match`", "Built-in validators"],
        ["`default`", "A value (or function) used when the field is missing"],
        ["`trim`, `lowercase`, `uppercase`", "Setters that clean values before saving"],
        ["`unique: true`", "Creates a unique **index**. It isn’t a validator, so handle duplicate-key error 11000"],
        ["`index: true`", "Creates a single-field index"],
        ["`select: false`", "Excluded from queries by default (e.g. `passwordHash`)"],
        ["`timestamps: true`", "Manages `createdAt` and `updatedAt`"]
      ] },

      { type: "h2", text: "CRUD with Mongoose" },
      { type: "code", language: "javascript", code: `const course = await Course.create({ title: "DSA", slug: "dsa", price: 4999, instructor: userId });

const list = await Course.find({ isPublished: true })
  .select("title slug price level")
  .sort({ createdAt: -1 })
  .limit(20)
  .lean();                              // plain objects: faster, less memory

const one = await Course.findOne({ slug: "dsa" });

const updated = await Course.findByIdAndUpdate(
  id,
  { $set: { price: 3999 } },
  { new: true, runValidators: true }    // return the new doc and validate the update
);

await Course.deleteOne({ _id: id });` },
      { type: "callout", variant: "warning", text: "Validators run on `save()` and `create()`, but **not** on update queries such as `updateOne` or `findByIdAndUpdate` unless you pass `runValidators: true`." },

      { type: "h2", text: "Handling validation and duplicate errors" },
      { type: "code", language: "javascript", code: `try {
  await Course.create(req.body);
} catch (err) {
  if (err.name === "ValidationError") {
    const details = Object.values(err.errors).map((e) => ({ field: e.path, message: e.message }));
    return res.status(400).json({ error: { code: "VALIDATION_ERROR", details } });
  }
  if (err.code === 11000) {   // duplicate key from a unique index
    return res.status(409).json({ error: { code: "DUPLICATE", fields: Object.keys(err.keyValue) } });
  }
  throw err;
}` },

      { type: "h2", text: "Relationships with populate" },
      { type: "code", language: "javascript", code: `const course = await Course.findOne({ slug: "dsa" })
  .populate("instructor", "name avatar")   // only the fields you need
  .lean();

// course.instructor → { _id, name: "Ravi", avatar: "..." } instead of just an ObjectId` },
      { type: "p", text: "`populate` runs a **second query** behind the scenes (`User.find({ _id: { $in: [...] } })`). It is convenient, but populating inside loops or several levels deep multiplies queries. For heavy reporting, use an aggregation with `$lookup`, or embed the fields you need." },
      { type: "h3", text: "Virtual populate: reverse relationships" },
      { type: "code", language: "javascript", code: `courseSchema.virtual("reviews", {
  ref: "Review",
  localField: "_id",
  foreignField: "course",
  options: { sort: { createdAt: -1 }, limit: 5 }
});

const course = await Course.findById(id).populate("reviews");` },

      { type: "h2", text: "Middleware (hooks), methods and statics" },
      { type: "code", language: "javascript", code: `import bcrypt from "bcrypt";

const userSchema = new mongoose.Schema({
  email: { type: String, required: true, unique: true, lowercase: true },
  passwordHash: { type: String, required: true, select: false },
  role: { type: String, enum: ["student", "admin"], default: "student" }
});

// pre-save hook: hash the password only when it changed
userSchema.pre("save", async function () {
  if (!this.isModified("passwordHash")) return;
  this.passwordHash = await bcrypt.hash(this.passwordHash, 12);
});

// instance method
userSchema.methods.checkPassword = function (plain) {
  return bcrypt.compare(plain, this.passwordHash);
};

// static method
userSchema.statics.findByEmail = function (email) {
  return this.findOne({ email: email.toLowerCase() }).select("+passwordHash");
};

// never send the hash to clients
userSchema.set("toJSON", {
  transform: (doc, ret) => { delete ret.passwordHash; delete ret.__v; return ret; }
});` },
      { type: "callout", variant: "note", text: "`pre(\"save\")` hooks don’t run for `updateOne` or `findOneAndUpdate`. Those have separate query middleware (`pre(\"findOneAndUpdate\")`). Use regular `function` syntax in hooks, not arrow functions, so `this` refers to the document." },

      { type: "h2", text: "lean(): when you don’t need documents" },
      { type: "table", headers: ["", "Default (hydrated)", "`.lean()`"], rows: [
        ["Returns", "Mongoose documents with getters, `save()` and change tracking", "Plain JavaScript objects"],
        ["Speed and memory", "Slower and heavier", "Often several times faster and lighter"],
        ["Use for", "Load → modify → `save()`", "Read-only API responses (most GET endpoints)"]
      ] },

      { type: "h2", text: "Transactions" },
      { type: "p", text: "When several documents must change together or not at all, such as creating an enrolment **and** decrementing seats, use a transaction. This requires a replica set, which MongoDB Atlas always provides:" },
      { type: "code", language: "javascript", code: `const session = await mongoose.startSession();
try {
  await session.withTransaction(async () => {
    const result = await Cohort.updateOne(
      { _id: cohortId, seatsLeft: { $gt: 0 } },
      { $inc: { seatsLeft: -1 } },
      { session }
    );
    if (result.modifiedCount === 0) throw new Error("Batch is full");

    await Enrollment.create([{ studentId, cohortId }], { session });
  });
} finally {
  await session.endSession();
}` },
      { type: "callout", variant: "tip", text: "Transactions have overhead, so use them only when you need them. Often a single atomic update on a well-designed document does the job. Mention this trade-off in interviews." },

      { type: "h2", text: "Connection best practices" },
      { type: "code", language: "javascript", code: `await mongoose.connect(process.env.MONGO_URI, {
  maxPoolSize: 10,                 // concurrent connections per process
  serverSelectionTimeoutMS: 5000   // fail fast if the database is unreachable
});

mongoose.connection.on("error", (err) => logger.error({ err }, "mongo error"));` },
      { type: "ul", items: [
        "Connect **once** at startup and reuse the connection. Never connect per request.",
        "In serverless functions, cache the connection on a global variable between invocations.",
        "Set `autoIndex: false` in production and create indexes through migrations, so startup doesn’t build large indexes."
      ] },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "What is the difference between a Mongoose schema and a model?", answer: ["A schema defines the structure, types, validators, defaults, indexes, hooks and methods. A model is a constructor compiled from the schema and bound to a collection. It creates documents and provides query methods such as `find`, `create` and `updateOne`."] },
      { type: "qa", question: "Is `unique: true` a validator?", answer: ["No. It tells Mongoose to build a unique index in MongoDB. Violations surface as a MongoDB duplicate-key error (code 11000), not a ValidationError, so you must handle that separately, typically returning 409 Conflict."] },
      { type: "qa", question: "What does `.lean()` do and when should you use it?", answer: ["It returns plain JavaScript objects instead of full Mongoose documents, which skips hydration, getters and change tracking. It is significantly faster and uses less memory, which makes it ideal for read-only queries that are sent straight to clients."] },
      { type: "qa", question: "How does `populate` work, and what are its drawbacks?", answer: ["It replaces referenced ObjectIds with documents from another collection by running additional queries with `$in`. Drawbacks: extra round trips, N+1-style query explosions when nested or used in loops, and less control than `$lookup`. Select only the needed fields, or denormalise hot fields."] },
      { type: "qa", question: "Why aren’t validators running on my update?", answer: ["Mongoose runs schema validators on `save()` and `create()` by default, not on update queries. Pass `{ runValidators: true }`, ideally set globally. Update validators also have limitations, for example `this` isn’t the document."] },

      { type: "exercise", title: "Build an Enrollment model", task: "Create an `Enrollment` schema with `student` (ref User, required), `course` (ref Course, required), `progress` (0–100, default 0), `status` (\"active\", \"completed\" or \"dropped\", default \"active\") and timestamps. Prevent duplicate enrolments of the same student in the same course, set `status` to \"completed\" automatically when progress reaches 100 on save, and add a static `forStudent(studentId)` that returns the student’s active enrolments with course titles, as plain objects.", language: "javascript", solution: `const enrollmentSchema = new mongoose.Schema(
  {
    student: { type: mongoose.Schema.Types.ObjectId, ref: "User", required: true },
    course: { type: mongoose.Schema.Types.ObjectId, ref: "Course", required: true },
    progress: { type: Number, min: 0, max: 100, default: 0 },
    status: { type: String, enum: ["active", "completed", "dropped"], default: "active" }
  },
  { timestamps: true }
);

// One enrollment per student per course (also serves "by student" queries)
enrollmentSchema.index({ student: 1, course: 1 }, { unique: true });

enrollmentSchema.pre("save", function () {
  if (this.isModified("progress") && this.progress === 100) {
    this.status = "completed";
  }
});

enrollmentSchema.statics.forStudent = function (studentId) {
  return this.find({ student: studentId, status: "active" })
    .populate("course", "title slug")
    .sort({ updatedAt: -1 })
    .lean();
};

export const Enrollment = mongoose.model("Enrollment", enrollmentSchema);`, explanation: "The compound unique index enforces the business rule at the database level, which is safe even with concurrent requests, and its `student` prefix also speeds up `forStudent`. The pre-save hook keeps `status` consistent, and the static gives controllers one clear, efficient query." },

      { type: "summary", items: [
        "Schemas define shape, validation, defaults, indexes and hooks. Models query and create documents.",
        "Pass `runValidators: true` for updates, and handle duplicate-key error 11000 separately from validation errors.",
        "`populate` is a convenient second query. Select fields and avoid deep or looped populates.",
        "Use `.lean()` for read-only responses, and hide sensitive fields with `select: false` and `toJSON`.",
        "Use transactions only for true multi-document atomicity, and connect once with a pooled connection."
      ] }
    ]
  }
];
